import { CaretRightOutlined } from '@ant-design/icons';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { Collapse, CollapseProps, Flex, Menu as MenuAnt, MenuProps, Typography, theme } from 'antd';
import { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { getHandleStatusCount, getMyStatusCount } from 'src/apis/helpdesk/ticket.api';
import {
  getMyAdditionalWorkingTimeTicketCount,
  getAdditionalWorkingTimeTicketApprovalCount,
  getAdditionalWorkingTimeTicketHrConfirmCount
} from 'src/apis/hr_ticket/additionalWorkingTimeTicket.api';
import {
  getAdvancePaymentTicketApprovalCount,
  getMyAdvancePaymentTicketCount
} from 'src/apis/hr_ticket/advancePaymentTicket.api';
import {
  getBusinessTripTicketApprovalCount,
  getBusinessTripTicketHrConfirmCount,
  getMyBusinessTripTicketCount
} from 'src/apis/hr_ticket/businessTripTicket.api';
import {
  getEquipmentSupplyRequestTicketApprovalCount,
  getMyEquipmentSupplyRequestTicketCount
} from 'src/apis/hr_ticket/equipmentSupplyRequestTicket.api';
import {
  getLeaveTicketApprovalCount,
  getLeaveTicketHrConfirmCount,
  getMyLeaveTicketCount
} from 'src/apis/hr_ticket/leaveTicket.api';
import {
  getMyOvertimeTicketCount,
  getOvertimeTicketApprovalCount,
  getOvertimeTicketHrConfirmCount
} from 'src/apis/hr_ticket/overtimeTicket.api';
import {
  getMyPurchaseRequestTicketCount,
  getPurchaseRequestTicketApprovalCount
} from 'src/apis/hr_ticket/purchaseRequestTicket.api';
import {
  getMyTransportationTicketCount,
  getTransportationHrConfirmCount,
  getTransportationTicketApprovalCount
} from 'src/apis/hr_ticket/transportationTicket.api';
import {
  getResignationTicketApprovalCount,
  getResignationTicketHrConfirmCount,
  getMyResignationTicketCount
} from 'src/apis/hr_ticket/resignationTicket.api';
import { getHandoverTicketApprovalCount, getMyHandoverTicketCount } from 'src/apis/office-ticket/handoverTicket.api';
import urlPath from 'src/constants/urlPath';
import { DepartmentEn } from 'src/constants/department.enum';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { SegmentedEn } from 'src/constants/segmentedEn.enum';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import { AppContext } from 'src/contexts/app.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { UserInfo } from 'src/types/master_data/employee.type';
import { ApprovalStatus, ApprovalStatusCount, TicketStatus, TicketStatusCount } from 'src/types/helpdesk/ticket.type';
import { getPathsFromRoles, scrollToElement } from 'src/utils/utils';
import './menu.scss';

const { Text } = Typography;

interface HrLocation {
  myTicket?: string;
  approval?: string;
  hrConfirm?: string;
  supervisorConfirm?: string;
}

type MenuParamsType = {
  panelStyle: React.CSSProperties;
  segmented: SegmentedEn;
  navigate: NavigateFunction;
  location: any;
  approvalStatus: number | undefined;
  ticketStatusId: number | undefined;
  handleClickMenu: (status: string) => void;
  handleClickMenuHeader: (url?: string) => void;
  statusCountQuery: UseQueryResult<AxiosResponse<any, any> | null, unknown>;
  handleGetHrLocation: () => HrLocation;
  userInfo: UserInfo | null;
  basePath: string | undefined;
  paths?: string[];
  confirmType: ConfirmTypeEn | undefined;
  isConfirm: ConfirmTypeEn | undefined;
  isSupervisorConfirmed: ConfirmTypeEn | undefined;
};

type MenuItem = Required<MenuProps>['items'][number];

const getItem =
  (label: string, statusCount: number, loading: boolean, isWaitingApprove?: boolean) =>
  (key?: React.Key | null, icon?: React.ReactNode, children?: MenuItem[], type?: 'group'): MenuItem => {
    const itemLabel = (
      <Flex justify='space-between'>
        <Text>{label}</Text>
        {!loading && (
          <Text className={`sidebar-menu-extra ${isWaitingApprove ? 'waiting-approve' : ''}`}>{statusCount || ''}</Text>
        )}
      </Flex>
    );

    return {
      key,
      icon,
      children,
      label: itemLabel,
      type
    } as MenuItem;
  };

const itemsApprovalHandleMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ phê duyệt',
    statusCount?.waiting,
    loading
  )(JSON.stringify({ approvalStatus: ApprovalStatus.Waiting }), null),
  getItem('Đã duyệt', statusCount?.approved, loading)(JSON.stringify({ approvalStatus: ApprovalStatus.Approve }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ approvalStatus: ApprovalStatus.Reject }), null)
];

const itemsAcceptHandleMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ tiếp nhận',
    statusCount?.waiting,
    loading
  )(JSON.stringify({ approvalStatus: ApprovalStatus.Waiting }), null),
  getItem(
    'Đã tiếp nhận',
    statusCount?.approved,
    loading
  )(JSON.stringify({ approvalStatus: ApprovalStatus.Approve }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ approvalStatus: ApprovalStatus.Reject }), null)
];

const itemsSolutionHandleMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Đã tiếp nhận',
    statusCount?.approved,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.AcceptReceiveRequest }), null),
  getItem(
    'Đang xử lý',
    statusCount?.handling,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.Processing }), null),
  getItem(
    'Chờ xác nhận',
    statusCount?.waitingConfirm,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.HandleCompleted }), null),
  getItem('Lịch sử', 0, loading)(JSON.stringify({ ticketStatus: TicketStatus.HandleHistory }), null)
];

const itemsMyHandleMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Đang xử lý',
    statusCount?.handling,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.Processing }), null),
  getItem(
    'Chờ xác nhận',
    statusCount?.waitingConfirm,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.HandleCompleted }), null),
  getItem('Lịch sử xử lý', 0, loading)(JSON.stringify({ ticketStatus: TicketStatus.HandleHistory }), null)
];

const itemsMyTicketMenu = (statusCount: TicketStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ duyệt yêu cầu',
    statusCount?.new,
    loading,
    true
  )(JSON.stringify({ ticketStatus: TicketStatus.New }), null),
  getItem(
    'Đã duyệt',
    statusCount?.approvedRequest,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.ApprovedRequest }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ ticketStatus: TicketStatus.Reject }), null),
  getItem(
    'Đã tiếp nhận',
    statusCount?.acceptReceiveRequest,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.AcceptReceiveRequest }), null),
  getItem(
    'Đang xử lý',
    statusCount?.processing,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.Processing }), null),
  getItem(
    'Chờ xác nhận hoàn tất',
    statusCount?.handleCompleted,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.HandleCompleted }), null),
  getItem('Hoàn tất', statusCount?.finished, loading)(JSON.stringify({ ticketStatus: TicketStatus.Finished }), null),
  getItem('Hủy phiếu', statusCount?.canceled, loading)(JSON.stringify({ ticketStatus: TicketStatus.Canceled }), null)
];

// HR MENU ITEMS
const itemsMyTicketHrMenu = (statusCount: TicketStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ phê duyệt',
    statusCount?.pending,
    loading,
    true
  )(JSON.stringify({ ticketStatus: TicketStatus.New }), null),
  getItem(
    'Đã duyệt',
    statusCount?.approved,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.ApprovedRequest }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ ticketStatus: TicketStatus.Reject }), null),
  getItem('Hủy phiếu', statusCount?.canceled, loading)(JSON.stringify({ ticketStatus: TicketStatus.Canceled }), null)
];
const itemsMyPackageMenu = (statusCount: TicketStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ phê duyệt',
    statusCount?.pending,
    loading,
    true
  )(JSON.stringify({ ticketStatus: TicketStatus.New }), null),
  getItem(
    'Đã duyệt',
    statusCount?.approved,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.ApprovedRequest }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ ticketStatus: TicketStatus.Reject }), null),
  getItem('Hủy phiếu', statusCount?.canceled, loading)(JSON.stringify({ ticketStatus: TicketStatus.Canceled }), null)
];
const itemsMyTicketHrOvertimeTicketMenu = (statusCount: TicketStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ phê duyệt',
    statusCount?.pending,
    loading,
    true
  )(
    JSON.stringify({
      ticketStatus: TicketStatus.New,
      isSupervisorConfirmed: ConfirmTypeEn.WaitingConfirm,
      isConfirm: ConfirmTypeEn.WaitingConfirm
    }),
    null
  ),
  getItem(
    'Đã duyệt',
    statusCount?.approved,
    loading
  )(
    JSON.stringify({
      ticketStatus: TicketStatus.ApprovedRequest,
      isSupervisorConfirmed: ConfirmTypeEn.WaitingConfirm,
      isConfirm: ConfirmTypeEn.WaitingConfirm
    }),
    null
  ),
  getItem(
    'Đã từ chối',
    statusCount?.rejected,
    loading
  )(
    JSON.stringify({
      ticketStatus: TicketStatus.Reject,
      isSupervisorConfirmed: ConfirmTypeEn.WaitingConfirm,
      isConfirm: ConfirmTypeEn.WaitingConfirm
    }),
    null
  ),
  getItem(
    'Giám sát xác nhận',
    statusCount?.confirmed,
    loading
  )(
    JSON.stringify({
      ticketStatus: TicketStatus.ApprovedRequest,
      isSupervisorConfirmed: ConfirmTypeEn.Confirmed,
      isConfirm: ConfirmTypeEn.WaitingConfirm
    }),
    null
  ),
  getItem(
    'Người Y/C xác nhận',
    statusCount?.completed,
    loading
  )(
    JSON.stringify({
      ticketStatus: TicketStatus.ApprovedRequest,
      isSupervisorConfirmed: ConfirmTypeEn.Confirmed,
      isConfirm: ConfirmTypeEn.Confirmed
    }),
    null
  ),
  getItem(
    'Hủy phiếu',
    statusCount?.canceled,
    loading
  )(
    JSON.stringify({
      ticketStatus: TicketStatus.Canceled,
      isSupervisorConfirmed: ConfirmTypeEn.WaitingConfirm,
      isConfirm: ConfirmTypeEn.WaitingConfirm
    }),
    null
  )
];

const itemsConfirmSupervisorHrOvertimeTicketMenu = (
  statusCount: ApprovalStatusCount,
  loading: boolean
): MenuProps['items'] => [
  getItem(
    'Chờ xác nhận',
    statusCount?.waitingConfirm,
    loading
  )(
    JSON.stringify({
      isSupervisorConfirmed: ConfirmTypeEn.WaitingConfirm
    }),
    null
  ),
  getItem(
    'Đã xác nhận',
    statusCount?.finished,
    loading
  )(
    JSON.stringify({
      isSupervisorConfirmed: ConfirmTypeEn.Confirmed
    }),
    null
  ),
  getItem(
    'Đã từ chối',
    statusCount?.rejected,
    loading
  )(
    JSON.stringify({
      isSupervisorConfirmed: ConfirmTypeEn.Reject
    }),
    null
  )
];

const itemsMyTicketHrTransportationMenu = (statusCount: TicketStatusCount, loading: boolean): MenuProps['items'] => [
  getItem('Chờ phê duyệt', statusCount?.pending, loading)(JSON.stringify({ ticketStatus: TicketStatus.New }), null),
  getItem(
    'Đã duyệt',
    statusCount?.approved,
    loading,
    true
  )(JSON.stringify({ ticketStatus: TicketStatus.ApprovedRequest }), null),
  getItem(
    'Đã từ chối phê duyệt',
    statusCount?.rejected,
    loading
  )(JSON.stringify({ ticketStatus: TicketStatus.Reject }), null),
  getItem(
    'Đã xác nhận',
    statusCount?.confirmed,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.Confirmed }), null),
  getItem(
    'Đã từ chối xác nhận',
    statusCount?.rejectedConfirm,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.Reject }), null),
  getItem('Hủy phiếu', statusCount?.canceled, loading)(JSON.stringify({ ticketStatus: TicketStatus.Canceled }), null)
];

const itemsHandleHrMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ phê duyệt',
    statusCount?.waiting,
    loading,
    true
  )(JSON.stringify({ approvalStatus: ApprovalStatus.Waiting }), null),
  getItem('Đã duyệt', statusCount?.approved, loading)(JSON.stringify({ approvalStatus: ApprovalStatus.Approve }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ approvalStatus: ApprovalStatus.Reject }), null)
];

const itemsConfirmHandleHrMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ xác nhận',
    statusCount?.waitingConfirm,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.WaitingConfirm }), null),
  getItem(
    'Đã xác nhận',
    statusCount?.finished,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.Confirmed }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ confirmType: ConfirmTypeEn.Reject }), null)
];

// OFFICEF MENU ITEMS
const itemsMyTicketOfficeMenu = (statusCount: TicketStatusCount, loading: boolean): MenuProps['items'] => [
  getItem('Chờ xác nhận', statusCount?.pending, loading)(JSON.stringify({ ticketStatus: TicketStatus.New }), null),
  getItem('Hoàn tất', statusCount?.approved, loading)(JSON.stringify({ ticketStatus: TicketStatus.Finished }), null),
  getItem('Hủy phiếu', statusCount?.canceled, loading)(JSON.stringify({ ticketStatus: TicketStatus.Canceled }), null)
];
const itemsHandleOfficeMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ xác nhận',
    statusCount?.waiting,
    loading
  )(JSON.stringify({ approvalStatus: ApprovalStatus.Waiting }), null),
  getItem(
    'Đã xác nhận',
    statusCount?.approved,
    loading
  )(JSON.stringify({ approvalStatus: ApprovalStatus.Approve }), null)
];

// HR CONFIRM MENU ITEMS
const itemsHrConfirmMenu = (statusCount: ApprovalStatusCount, loading: boolean): MenuProps['items'] => [
  getItem(
    'Chờ xác nhận',
    statusCount?.waitingConfirm,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.WaitingConfirm }), null),
  getItem(
    'Đã xác nhận',
    statusCount?.finished,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.Confirmed }), null),
  getItem(
    'Đã đồng bộ',
    statusCount?.synchronized,
    loading
  )(JSON.stringify({ confirmType: ConfirmTypeEn.Synced }), null),
  getItem('Đã từ chối', statusCount?.rejected, loading)(JSON.stringify({ confirmType: ConfirmTypeEn.Reject }), null)
];

const getItemsTicket: (params: any) => CollapseProps['items'] = (params) => {
  if (params.segmented === SegmentedEn.myTicket) {
    const statusCount: TicketStatusCount = params.statusCountQuery?.data?.data;

    return [
      {
        key: urlPath.myTicket.location,
        label: (
          <div onClick={() => params.handleClickMenuHeader(urlPath.myTicket.location)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.myTicket.location) && 'active'
              }`}
            >
              Ticket của tôi
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={urlPath.myTicket.location}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsMyTicketMenu(statusCount as TicketStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ ticketStatus: params.ticketStatusId })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className={`sidebar-menu-header sidebar-menu-extra`}>
            {/* {Object.values(statusCount)} */}
            {!params.statusCountQuery.isLoading && (statusCount?.new || '')}
          </Text>
        )
      }
    ];
  } else {
    const approvalStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.countWaitingApprove;
    const confirmStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.countConfirm;
    const myHandleStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.countMyHandle;
    const handleStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.countHandle;

    return [
      {
        key: urlPath.approval.location,
        label: (
          <div onClick={() => params.handleClickMenuHeader(urlPath.approval.location)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.approval.location) && 'active'
              }`}
            >
              Phê duyệt
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={urlPath.approval.location}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsApprovalHandleMenu(
              approvalStatusCount as ApprovalStatusCount,
              params.statusCountQuery.isLoading
            )}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (approvalStatusCount?.waiting || '')}
          </Text>
        )
      },
      {
        key: urlPath.accept.location,
        label: (
          <div onClick={() => params.handleClickMenuHeader(urlPath.accept.location)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.accept.location) && 'active'
              }`}
            >
              Tiếp nhận
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={2}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsAcceptHandleMenu(confirmStatusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (confirmStatusCount?.waiting || '')}
          </Text>
        )
      },
      {
        key: urlPath.solution.location,
        label: (
          <div onClick={() => params.handleClickMenuHeader(urlPath.solution.location)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.solution.location) && 'active'
              }`}
            >
              Xử lý của phòng
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={3}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsSolutionHandleMenu(handleStatusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ ticketStatus: params.ticketStatusId })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (approvalStatusCount?.approved || '')}
          </Text>
        )
      },
      {
        key: urlPath.handle.location,
        label: (
          <div onClick={() => params.handleClickMenuHeader(urlPath.handle.location)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.handle.location) && 'active'
              }`}
            >
              Phiếu của tôi xử lý
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={4}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsMyHandleMenu(myHandleStatusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ ticketStatus: params.ticketStatusId })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (myHandleStatusCount?.handling || '')}
          </Text>
        )
      }
    ];
  }
};

const getItemsHr: (params: MenuParamsType) => CollapseProps['items'] = (params) => {
  const statusCount: TicketStatusCount = params.statusCountQuery?.data?.data;

  const items = params.basePath?.includes(urlPath.myTransportationTicket.location)
    ? itemsMyTicketHrTransportationMenu(statusCount as TicketStatusCount, params.statusCountQuery.isLoading)
    : params.basePath?.includes(urlPath.myOvertimeTicket.location)
    ? itemsMyTicketHrOvertimeTicketMenu(statusCount as TicketStatusCount, params.statusCountQuery.isLoading)
    : itemsMyTicketHrMenu(statusCount as TicketStatusCount, params.statusCountQuery.isLoading);
  const urlToPackageMap = {
    [urlPath.leaveTicket.location]: urlPath.myLeavePackage.location
    // [urlPath.additionalWorkingTimeTicket.location]: urlPath.myAdditionalWorkingTimePackage.location
  };

  const packageType = Object.keys(urlToPackageMap).find((url) => params.basePath?.includes(url));

  const packageMenuPath = packageType ? urlToPackageMap[packageType] : undefined;

  const packageItems = itemsMyPackageMenu(statusCount as TicketStatusCount, params.statusCountQuery.isLoading);
  if (params.segmented === SegmentedEn.myTicket) {
    return [
      {
        key: params.handleGetHrLocation().myTicket,
        label: (
          <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().myTicket)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(params.handleGetHrLocation().myTicket) && 'active'
              }`}
            >
              Phiếu của tôi
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={params.handleGetHrLocation().myTicket}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={items}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[
              `${JSON.stringify({
                ticketStatus: params.ticketStatusId,
                isSupervisorConfirmed: params.isSupervisorConfirmed,
                isConfirm: params.isConfirm
              })}`
            ]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (statusCount?.pending || '')}
          </Text>
        )
      },
      packageType === urlPath.leaveTicket.location
        ? {
            key: packageMenuPath,
            label: (
              <div onClick={() => params.handleClickMenuHeader(packageMenuPath)}>
                <Text
                  className={`sidebar-menu-header ${params.location.pathname.includes(packageMenuPath) && 'active'}`}
                >
                  Package của tôi
                </Text>
              </div>
            ),
            children: (
              <MenuAnt
                key={packageMenuPath}
                style={{ backgroundColor: 'transparent' }}
                mode='inline'
                items={packageItems}
                onClick={(item) => params.handleClickMenu(item.key)}
                selectedKeys={[
                  `${JSON.stringify({
                    ticketStatus: params.ticketStatusId
                  })}`
                ]}
              />
            ),
            style: params.panelStyle,
            extra: (
              <Text className='sidebar-menu-header sidebar-menu-extra'>
                {!params.statusCountQuery.isLoading && (statusCount?.pending || '')}
              </Text>
            )
          }
        : undefined
    ].filter((item) => item !== undefined);
  } else {
    // Handle HR ticket
    const statusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data;

    // Phiếu vận chuyển
    if (params.handleGetHrLocation().approval === urlPath.transportationTicketApproval.location) {
      const approvalStatusCount = params.statusCountQuery?.data?.data?.handleTicket;
      const confirmStatusCount = params.statusCountQuery?.data?.data?.confirmTicket;
      // const driverHandleStatusCount = params.statusCountQuery?.data?.data?.driverHandle;
      return [
        {
          key: params.handleGetHrLocation().approval,
          label: (
            <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().approval)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(params.handleGetHrLocation().approval) && 'active'
                }`}
              >
                Phê duyệt
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={params.handleGetHrLocation().approval}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(approvalStatusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (approvalStatusCount?.waiting || '')}
            </Text>
          )
        },
        {
          key: urlPath.transportationTicketConfirm.location,
          label: (
            <div onClick={() => params.handleClickMenuHeader(urlPath.transportationTicketConfirm.location)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(urlPath.transportationTicketConfirm.location) && 'active'
                }`}
              >
                Xác nhận
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={urlPath.transportationTicketConfirm.location}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsConfirmHandleHrMenu(
                confirmStatusCount as ApprovalStatusCount,
                params.statusCountQuery.isLoading
              )}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ confirmType: params.confirmType })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (confirmStatusCount?.waitingConfirm || '')}
            </Text>
          )
        }
      ];
      // if (
      //   params.paths?.includes(urlPath.transportationTicketConfirm.location) &&
      //   params.userInfo?.departmentId !== DepartmentEn.hr
      // ) {
      // } else {
      //   return [
      //     {
      //       key: params.handleGetHrLocation().approval,
      //       label: (
      //         <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().approval)}>
      //           <Text
      //             className={`sidebar-menu-header ${
      //               params.location.pathname.includes(params.handleGetHrLocation().approval) && 'active'
      //             }`}
      //           >
      //             Phê duyệt
      //           </Text>
      //         </div>
      //       ),
      //       children: (
      //         <MenuAnt
      //           key={params.handleGetHrLocation().approval}
      //           style={{ backgroundColor: 'transparent' }}
      //           mode='inline'
      //           items={itemsHandleHrMenu(approvalStatusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
      //           onClick={(item) => {
      //             params.handleClickMenu(item.key);
      //           }}
      //           selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
      //         />
      //       ),
      //       style: params.panelStyle,
      //       extra: (
      //         <Text className='sidebar-menu-header sidebar-menu-extra'>
      //           {!params.statusCountQuery.isLoading && (approvalStatusCount?.waiting || '')}
      //         </Text>
      //       )
      //     }
      //   ];
      // }
    }
    // Phiếu tăng ca
    else if (params.handleGetHrLocation().approval === urlPath.overtimeTicketApproval.location) {
      const approvalStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.handleTicket;
      const confirmStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.confirmTicket;
      return [
        {
          key: params.handleGetHrLocation().approval,
          label: (
            <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().approval)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(params.handleGetHrLocation().approval) && 'active'
                }`}
              >
                Phê duyệt
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={params.handleGetHrLocation().approval}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(approvalStatusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (approvalStatusCount?.waiting || '')}
            </Text>
          )
        },
        {
          key: urlPath.overtimeTicketSupervisorConfirm.location,
          label: (
            <div onClick={() => params.handleClickMenuHeader(urlPath.overtimeTicketSupervisorConfirm.location)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(urlPath.overtimeTicketSupervisorConfirm.location) && 'active'
                }`}
              >
                Giám sát xác nhận
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={urlPath.overtimeTicketSupervisorConfirm.location}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsConfirmSupervisorHrOvertimeTicketMenu(
                confirmStatusCount as ApprovalStatusCount,
                params.statusCountQuery.isLoading
              )}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[
                `${JSON.stringify({
                  isSupervisorConfirmed: params.isSupervisorConfirmed
                })}`
              ]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (confirmStatusCount?.waitingConfirm || '')}
            </Text>
          )
        }
      ];
    }
    // Phiếu nghỉ phép
    else if (params.handleGetHrLocation().approval === urlPath.leaveTicketApproval.location) {
      const approvalLeaveStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.handleLeaveTicket;
      const approvalLeavePackageStatusCount: ApprovalStatusCount =
        params.statusCountQuery?.data?.data?.handleLeavePackage;

      return [
        {
          key: params.handleGetHrLocation().approval,
          label: (
            <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().approval)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname === params.handleGetHrLocation().approval && 'active'
                }`}
              >
                Phê duyệt
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={params.handleGetHrLocation().approval}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(
                approvalLeaveStatusCount as ApprovalStatusCount,
                params.statusCountQuery.isLoading
              )}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (approvalLeaveStatusCount?.waiting || '')}
            </Text>
          )
        },
        {
          key: urlPath.leavePackageApproval.location,
          label: (
            <div onClick={() => params.handleClickMenuHeader(urlPath.leavePackageApproval.location)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(urlPath.leavePackageApproval.location) && 'active'
                }`}
              >
                Phê duyệt package
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={urlPath.leavePackageApproval.location}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(
                approvalLeavePackageStatusCount as ApprovalStatusCount,
                params.statusCountQuery.isLoading
              )}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (approvalLeavePackageStatusCount?.waiting || '')}
            </Text>
          )
        }
      ];
    }
    // Phiếu nghỉ việc
    else if (params.handleGetHrLocation().approval === urlPath.resignationTicketApproval.location) {
      const approvalResignationStatusCount: ApprovalStatusCount =
        params.statusCountQuery?.data?.data?.handleResignationTicket;

      return [
        {
          key: params.handleGetHrLocation().approval,
          label: (
            <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().approval)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname === params.handleGetHrLocation().approval && 'active'
                }`}
              >
                Phê duyệt
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={params.handleGetHrLocation().approval}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(
                approvalResignationStatusCount as ApprovalStatusCount,
                params.statusCountQuery.isLoading
              )}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (approvalResignationStatusCount?.waiting || '')}
            </Text>
          )
        }
      ];
    } else if (params.handleGetHrLocation().approval === urlPath.additionalWorkingTimeTicketApproval.location) {
      const approvalAdditionalWorkingTimeTicketStatusCount: ApprovalStatusCount =
        params.statusCountQuery?.data?.data?.handleAdditionalWorkingTimeTicket;
      return [
        {
          key: urlPath.additionalWorkingTimeTicketApproval.location,
          label: (
            <div onClick={() => params.handleClickMenuHeader(urlPath.additionalWorkingTimeTicketApproval.location)}>
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(urlPath.additionalWorkingTimeTicketApproval.location) && 'active'
                }`}
              >
                Phê duyệt
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={urlPath.additionalWorkingTimeTicketApproval.location}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(
                approvalAdditionalWorkingTimeTicketStatusCount as ApprovalStatusCount,
                params.statusCountQuery.isLoading
              )}
              onClick={(item) => {
                params.handleClickMenu(item.key);
              }}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (approvalAdditionalWorkingTimeTicketStatusCount?.waiting || '')}
            </Text>
          )
        }
      ];
    }
    // Các phiếu HR còn lại
    else {
      return [
        {
          key: params.handleGetHrLocation().approval,
          label: (
            <div
              onClick={() => {
                console.log(params.handleGetHrLocation().approval);
                return;
                // params.handleClickMenuHeader(params.handleGetHrLocation().approval)
              }}
            >
              <Text
                className={`sidebar-menu-header ${
                  params.location.pathname.includes(params.handleGetHrLocation().approval) && 'active'
                }`}
              >
                Phê duyệt
              </Text>
            </div>
          ),
          children: (
            <MenuAnt
              key={params.handleGetHrLocation().approval}
              style={{ backgroundColor: 'transparent' }}
              mode='inline'
              items={itemsHandleHrMenu(statusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
              onClick={(item) => params.handleClickMenu(item.key)}
              selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
            />
          ),
          style: params.panelStyle,
          extra: (
            <Text className='sidebar-menu-header sidebar-menu-extra'>
              {!params.statusCountQuery.isLoading && (statusCount?.waiting || '')}
            </Text>
          )
        }
      ];
    }
  }
};

const getItemsOffice: (params: any) => CollapseProps['items'] = (params) => {
  const statusCount: TicketStatusCount = params.statusCountQuery?.data?.data;

  if (params.segmented === SegmentedEn.myTicket) {
    return [
      {
        key: params.handleGetHrLocation().myTicket,
        label: (
          <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().myTicket)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(params.handleGetHrLocation().myTicket) && 'active'
              }`}
            >
              Phiếu của tôi
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={params.handleGetHrLocation().myTicket}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsMyTicketOfficeMenu(statusCount as TicketStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ ticketStatus: params.ticketStatusId })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (statusCount?.pending || '')}
          </Text>
        )
      }
    ];
  } else {
    const statusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data;
    return [
      {
        key: params.handleGetHrLocation().approval,
        label: (
          <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().approval)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(params.handleGetHrLocation().approval) && 'active'
              }`}
            >
              Xác nhận
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={params.handleGetHrLocation().approval}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsHandleOfficeMenu(statusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ approvalStatus: params.approvalStatus })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (statusCount?.waiting || '')}
          </Text>
        )
      }
    ];
  }
};

const getItemsHrConfirm: (params: MenuParamsType) => CollapseProps['items'] = (params) => {
  const statusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data?.countHrConfirmTicketStatus;
  const transportationStatusCount: ApprovalStatusCount = params.statusCountQuery?.data?.data;

  // Bổ sung chấm công - admin xem tất cả, không có sub-menu filter
  if (params.location.pathname.includes(urlPath.hrConfirmAdditionalWorkingTimeTicket.location)) {
    const additionalStatusCount: TicketStatusCount = params.statusCountQuery?.data?.data;
    return [
      {
        key: params.handleGetHrLocation().hrConfirm,
        label: (
          <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().hrConfirm)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.hrConfirmAdditionalWorkingTimeTicket.location) && 'active'
              }`}
            >
              Danh sách phiếu
            </Text>
          </div>
        ),
        children: null,
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (additionalStatusCount?.pending || '')}
          </Text>
        )
      }
    ];
  } else if (
    params.paths?.includes(urlPath.hrConfirmTransportationTicket.location) &&
    params.location.pathname.includes(urlPath.hrConfirmTransportationTicket.location) &&
    params.userInfo?.departmentId === DepartmentEn.hr
  ) {
    return [
      {
        key: params.handleGetHrLocation().hrConfirm,
        label: (
          <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().hrConfirm)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.hrConfirmTransportationTicket.location) && 'active'
              }`}
            >
              Xác nhận phiếu
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={params.handleGetHrLocation().approval}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsConfirmHandleHrMenu(
              transportationStatusCount as ApprovalStatusCount,
              params.statusCountQuery.isLoading
            )}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ confirmType: params.confirmType })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (transportationStatusCount?.totalTicket || '')}
          </Text>
        )
      }
    ];
  } else
    return [
      {
        key: params.handleGetHrLocation().hrConfirm,
        label: (
          <div onClick={() => params.handleClickMenuHeader(params.handleGetHrLocation().hrConfirm)}>
            <Text
              className={`sidebar-menu-header ${
                params.location.pathname.includes(urlPath.hrConfirm.location) && 'active'
              }`}
            >
              Xác nhận phiếu
            </Text>
          </div>
        ),
        children: (
          <MenuAnt
            key={params.handleGetHrLocation().approval}
            style={{ backgroundColor: 'transparent' }}
            mode='inline'
            items={itemsHrConfirmMenu(statusCount as ApprovalStatusCount, params.statusCountQuery.isLoading)}
            onClick={(item) => params.handleClickMenu(item.key)}
            selectedKeys={[`${JSON.stringify({ confirmType: params.confirmType })}`]}
          />
        ),
        style: params.panelStyle,
        extra: (
          <Text className='sidebar-menu-header sidebar-menu-extra'>
            {!params.statusCountQuery.isLoading && (statusCount?.totalTicket || '')}
          </Text>
        )
      }
    ];
};

interface Props {
  segmented: SegmentedEn;
}

const Menu = ({ segmented }: Props) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useBasePath();
  const { roles, setTicketSelected, userInfo } = useContext(AppContext);
  const [currentMenuCollapse, setCurrentMenuCollapse] = useState<string>(location.pathname);
  const {
    approvalStatus,
    ticketStatusId,
    rangeDate,
    setApprovalStatus,
    setTicketStatusId,
    confirmType,
    setConfirmType,
    setTableParams,
    setIsSupervisorConfirmed,
    setIsConfirm,
    isConfirm,
    isSupervisorConfirmed
  } = useContext(TicketContext);
  const { setIsTicketDetailCollapsed } = useContext(AppContext);
  const isHrTicket = location.pathname.includes(urlPath.hrTicket.location);
  const isHrConfirm = location.pathname.includes(urlPath.hrConfirm.location);
  const isOfficeTicket = location.pathname.includes(urlPath.corporateOperation.location);

  const pathsFromRole = useMemo(() => {
    return getPathsFromRoles(roles);
  }, [roles]);

  const panelStyle: React.CSSProperties = {
    marginBottom: 10,
    borderRadius: token.borderRadiusLG,
    border: 'none'
  };

  useEffect(() => {
    const paths = [
      urlPath.myTicket.location,
      urlPath.approval.location,
      urlPath.accept.location,
      urlPath.solution.location,
      urlPath.handle.location,
      urlPath.myAdvancePaymentTicket.location,
      urlPath.advancePaymentTicketApproval.location,
      urlPath.myLeaveTicket.location,
      urlPath.myLeavePackage.location,
      urlPath.myAdditionalWorkingTimeTicket.location,
      urlPath.additionalWorkingTimeTicketApproval.location,
      urlPath.myResignationTicket.location,
      urlPath.resignationTicketApproval.location,
      urlPath.leavePackageApproval.location,
      urlPath.leaveTicketApproval.location,
      urlPath.myOvertimeTicket.location,
      urlPath.overtimeTicketApproval.location,
      urlPath.overtimeTicketSupervisorConfirm.location,
      urlPath.myHandoverTicket.location,
      urlPath.handoverTicketApproval.location,
      urlPath.myTransportationTicket.location,
      urlPath.transportationTicketApproval.location,
      urlPath.transportationTicketConfirm.location,
      urlPath.transportationTicketHandle.location,
      urlPath.myBusinessTripTicket.location,
      urlPath.businessTripTicketApproval.location,
      urlPath.myPurchaseRequestTicket.location,
      urlPath.purchaseRequestTicketApproval.location,
      urlPath.myEquipmentSupplyRequestTicket.location,
      urlPath.equipmentSupplyRequestTicketApproval.location,
      urlPath.myAdditionalWorkingTimeTicket.location,
      urlPath.hrConfirmLeaveTicket.location,
      urlPath.hrConfirmBusinessTripTicket.location,
      urlPath.hrConfirmOvertimeTicket.location,
      urlPath.hrConfirmTransportationTicket.location,
      urlPath.hrConfirmResignationTicket.location,
      urlPath.hrConfirmAdditionalWorkingTimeTicket.location
    ];

    const foundPath = paths.find((path) => basePath?.includes(path));

    if (foundPath) {
      setCurrentMenuCollapse(foundPath);
      if (foundPath === urlPath.myOvertimeTicket.location) {
        setIsConfirm(ConfirmTypeEn.LoadAll);
        setIsSupervisorConfirmed(ConfirmTypeEn.LoadAll);
      }
    }
  }, [basePath, setIsSupervisorConfirmed, setIsConfirm]);

  // handle call api menu count
  const handleCallApi = () => {
    const path = location.pathname;
    const pathSegmentedApiPairs = [
      {
        path: urlPath.ticket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyStatusCount
      },
      {
        path: urlPath.ticket.location,
        segmented: SegmentedEn.handle,
        apiFunc: getHandleStatusCount
      },
      {
        path: urlPath.myLeaveTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyLeaveTicketCount
      },
      {
        path: urlPath.leaveTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getLeaveTicketApprovalCount
      },
      {
        path: urlPath.myOvertimeTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyOvertimeTicketCount
      },
      {
        path: urlPath.overtimeTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getOvertimeTicketApprovalCount
      },
      {
        path: urlPath.overtimeTicketSupervisorConfirm.location,
        segmented: SegmentedEn.handle,
        apiFunc: getOvertimeTicketApprovalCount
      },
      {
        path: urlPath.myHandoverTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyHandoverTicketCount
      },
      {
        path: urlPath.handoverTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getHandoverTicketApprovalCount
      },
      {
        path: urlPath.myBusinessTripTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyBusinessTripTicketCount
      },
      {
        path: urlPath.businessTripTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getBusinessTripTicketApprovalCount
      },
      {
        path: urlPath.myPurchaseRequestTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyPurchaseRequestTicketCount
      },
      {
        path: urlPath.purchaseRequestTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getPurchaseRequestTicketApprovalCount
      },
      {
        path: urlPath.myAdvancePaymentTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyAdvancePaymentTicketCount
      },
      {
        path: urlPath.advancePaymentTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getAdvancePaymentTicketApprovalCount
      },
      {
        path: urlPath.myTransportationTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyTransportationTicketCount
      },
      {
        path: urlPath.transportationTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getTransportationTicketApprovalCount
      },
      {
        path: urlPath.transportationTicketConfirm.location,
        segmented: SegmentedEn.handle,
        apiFunc: getTransportationTicketApprovalCount
      },
      {
        path: urlPath.myEquipmentSupplyRequestTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyEquipmentSupplyRequestTicketCount
      },
      {
        path: urlPath.equipmentSupplyRequestTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getEquipmentSupplyRequestTicketApprovalCount
      },
      {
        path: urlPath.myAdditionalWorkingTimeTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyAdditionalWorkingTimeTicketCount
      },
      {
        path: urlPath.additionalWorkingTimeTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getAdditionalWorkingTimeTicketApprovalCount
      },
      {
        path: urlPath.hrConfirmLeaveTicket.location,
        segmented: SegmentedEn.hrConfirm,
        apiFunc: getLeaveTicketHrConfirmCount
      },
      {
        path: urlPath.hrConfirmBusinessTripTicket.location,
        segmented: SegmentedEn.hrConfirm,
        apiFunc: getBusinessTripTicketHrConfirmCount
      },
      {
        path: urlPath.hrConfirmOvertimeTicket.location,
        segmented: SegmentedEn.hrConfirm,
        apiFunc: getOvertimeTicketHrConfirmCount
      },
      {
        path: urlPath.hrConfirmTransportationTicket.location,
        segmented: SegmentedEn.hrConfirm,
        apiFunc: getTransportationHrConfirmCount,
        params: `${TransportationTypeEn.selfSufficient},${TransportationTypeEn.shuttle},${TransportationTypeEn.rentalCar}`
      },
      {
        path: urlPath.resignationTicket.location,
        segmented: SegmentedEn.myTicket,
        apiFunc: getMyResignationTicketCount
      },
      {
        path: urlPath.resignationTicketApproval.location,
        segmented: SegmentedEn.handle,
        apiFunc: getResignationTicketApprovalCount
      },
      {
        path: urlPath.hrConfirmResignationTicket.location,
        segmented: SegmentedEn.hrConfirm,
        apiFunc: getResignationTicketHrConfirmCount
      },
      {
        path: urlPath.hrConfirmAdditionalWorkingTimeTicket.location,
        segmented: SegmentedEn.hrConfirm,
        apiFunc: getAdditionalWorkingTimeTicketHrConfirmCount
      }
    ];

    const pair = pathSegmentedApiPairs.find((pair) => path.includes(pair.path) && segmented === pair.segmented);

    if (!pair) return null;

    return pair.apiFunc({ fromDate: rangeDate.fromDate, toDate: rangeDate.toDate }, String(pair.params));
  };

  const statusCountQuery = useQuery({
    queryKey: ['statusCount', basePath, rangeDate.fromDate, rangeDate.toDate, segmented],
    queryFn: () => {
      return handleCallApi();
    },
    staleTime: 60 * 1000,
    keepPreviousData: true
  });

  const getItems = () => {
    const menuParams: MenuParamsType = {
      panelStyle,
      segmented,
      navigate,
      location,
      approvalStatus,
      ticketStatusId,
      handleClickMenu,
      handleClickMenuHeader,
      statusCountQuery,
      handleGetHrLocation,
      userInfo: userInfo,
      basePath,
      confirmType,
      isConfirm,
      isSupervisorConfirmed
    };
    if (isHrTicket) {
      return getItemsHr({ ...menuParams, paths: pathsFromRole, confirmType: confirmType });
    } else if (isHrConfirm) {
      return getItemsHrConfirm({ ...menuParams, paths: pathsFromRole, confirmType: confirmType });
    } else if (isOfficeTicket) {
      return getItemsOffice(menuParams);
    } else {
      return getItemsTicket(menuParams);
    }
  };

  const handleClickMenu = (status: string) => {
    const { ticketStatus, approvalStatus, confirmType, isSupervisorConfirmed, isConfirm } = JSON.parse(status);

    setIsTicketDetailCollapsed(true);
    setTicketStatusId(ticketStatus ?? undefined);
    setApprovalStatus(approvalStatus ?? undefined);
    setConfirmType(confirmType ?? undefined);
    setIsSupervisorConfirmed(isSupervisorConfirmed ?? undefined);
    setIsConfirm(isConfirm ?? undefined);
    setTicketSelected(undefined);

    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1
      }
    }));
    scrollToElement('list-ticket');

    navigate(`${basePath}`);
  };

  const handleClickMenuHeader = (url?: string) => {
    if (url?.includes(urlPath.myOvertimeTicket.location)) {
      setIsConfirm(ConfirmTypeEn.LoadAll);
      setIsSupervisorConfirmed(ConfirmTypeEn.LoadAll);
    } else {
      setIsConfirm(undefined);
      setIsSupervisorConfirmed(undefined);
    }

    if (url) navigate(url);

    setTicketSelected(undefined);
    setIsTicketDetailCollapsed(true);
    setTicketStatusId(undefined);
    setApprovalStatus(undefined);
    setConfirmType(undefined);

    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1
      }
    }));
    scrollToElement('list-ticket');
  };

  const handleGetHrLocation = (): HrLocation => {
    const hrLocations = [
      {
        path: urlPath.leaveTicket.location,
        myTicket: urlPath.myLeaveTicket.location,
        approval: urlPath.leaveTicketApproval.location
      },
      {
        path: urlPath.transportationTicket.location,
        myTicket: urlPath.myTransportationTicket.location,
        approval: urlPath.transportationTicketApproval.location
      },
      {
        path: urlPath.overtimeTicket.location,
        myTicket: urlPath.myOvertimeTicket.location,
        approval: urlPath.overtimeTicketApproval.location
      },
      {
        path: urlPath.handoverTicket.location,
        myTicket: urlPath.myHandoverTicket.location,
        approval: urlPath.handoverTicketApproval.location
      },
      {
        path: urlPath.businessTripTicket.location,
        myTicket: urlPath.myBusinessTripTicket.location,
        approval: urlPath.businessTripTicketApproval.location
      },
      {
        path: urlPath.purchaseRequestTicket.location,
        myTicket: urlPath.myPurchaseRequestTicket.location,
        approval: urlPath.purchaseRequestTicketApproval.location
      },
      {
        path: urlPath.advancePaymentTicket.location,
        myTicket: urlPath.myAdvancePaymentTicket.location,
        approval: urlPath.advancePaymentTicketApproval.location
      },
      {
        path: urlPath.transportationTicket.location,
        myTicket: urlPath.myTransportationTicket.location,
        approval: urlPath.transportationTicketApproval.location
      },
      {
        path: urlPath.equipmentSupplyRequestTicket.location,
        myTicket: urlPath.myEquipmentSupplyRequestTicket.location,
        approval: urlPath.equipmentSupplyRequestTicketApproval.location
      },
      {
        path: urlPath.additionalWorkingTimeTicket.location,
        myTicket: urlPath.myAdditionalWorkingTimeTicket.location,
        approval: urlPath.additionalWorkingTimeTicketApproval.location
      },
      { path: urlPath.hrConfirmLeaveTicket.location, hrConfirm: urlPath.hrConfirmLeaveTicket.location },
      {
        path: urlPath.hrConfirmBusinessTripTicket.location,
        hrConfirm: urlPath.hrConfirmBusinessTripTicket.location
      },
      { path: urlPath.hrConfirmOvertimeTicket.location, hrConfirm: urlPath.hrConfirmOvertimeTicket.location },
      {
        path: urlPath.hrConfirmTransportationTicket.location,
        hrConfirm: urlPath.hrConfirmTransportationTicket.location
      },
      {
        path: urlPath.resignationTicket.location,
        myTicket: urlPath.myResignationTicket.location,
        approval: urlPath.resignationTicketApproval.location
      },
      { path: urlPath.hrConfirmResignationTicket.location, hrConfirm: urlPath.hrConfirmResignationTicket.location },
      {
        path: urlPath.hrConfirmAdditionalWorkingTimeTicket.location,
        hrConfirm: urlPath.hrConfirmAdditionalWorkingTimeTicket.location
      }
    ];

    const hrLocation = hrLocations.find((location) => basePath?.includes(location.path));

    return hrLocation || { myTicket: '', approval: '' };
  };

  return (
    <Flex vertical gap={5}>
      <Collapse
        bordered={false}
        collapsible='icon'
        activeKey={currentMenuCollapse}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} style={{ color: 'var(--color-primary)' }} />
        )}
        style={{ background: token.colorBgContainer }}
        items={getItems()}
        className='sidebar-menu'
        onChange={(key: string | string[]) => {}}
      />
    </Flex>
  );
};

export default Menu;
